import BrandLogo from '../components/BrandLogo';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Calendar, 
  TrendingUp, 
  Heart, 
  Briefcase, 
  Shield,
  Check,
  Star,
  ArrowRight,
  MessageCircle,
  Zap,
  Target,
  Users,
  HelpCircle
} from 'lucide-react';
import { fetchPublicPlans } from '../services/api';
import { describePromo, formatCurrency, getHorizonLabel, getPlanFeatures, sortPlans } from '../utils/planCatalog';

const LandingPage = () => {
  const navigate = useNavigate();

  const [plansResponse, setPlansResponse] = useState({ plans: [] });
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        setPlansLoading(true);
        const data = await fetchPublicPlans();
        if (!isMounted) return;

        if (data && data.success && Array.isArray(data.plans)) {
          setPlansResponse({
            plans: data.plans,
            generatedAt: data.generatedAt,
            currency: data.currency,
            year: data.year
          });
          setPlansError(null);
        } else {
          setPlansResponse({ plans: [] });
          setPlansError(data?.error || 'No fue posible cargar los planes.');
        }
      } catch (error) {
        if (!isMounted) return;
        setPlansResponse({ plans: [] });
        setPlansError(error.message || 'No fue posible cargar los planes.');
      } finally {
        if (isMounted) {
          setPlansLoading(false);
        }
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  // Calcular Energía Universal del día actual
  const sumDigits = (n) => Math.abs(n).toString().split("").reduce((a, b) => a + Number(b), 0);
  const reduceNum = (n) => { 
    let x = Math.abs(n); 
    while (x >= 10 && x !== 11 && x !== 22) x = sumDigits(x); 
    return x; 
  };

  const getCurrentUniversalDay = () => {
    const now = new Date();
    const D = now.getDate();
    const M = now.getMonth() + 1;
    const Y = now.getFullYear();
    
    const universalYear = reduceNum(Y);
    const universalMonth = reduceNum(universalYear + M);
    const universalDay = reduceNum(universalYear + M + D);
    
    return universalDay;
  };

  const todayUniversalEnergy = getCurrentUniversalDay();

  const pricingPlans = useMemo(() => {
    const rawPlans = plansResponse?.plans || [];
    return sortPlans(rawPlans).map((plan) => {
      const monthlyPrice = plan?.pricing?.prices?.monthly ?? null;
      const annualPrice = plan?.pricing?.prices?.annual ?? null;
      const promo = plan?.pricing?.promo && plan.pricing.promo.isActive ? plan.pricing.promo : null;

      // ✅ Solo usar features del catálogo (sin agregar extras que dupliquen)
      let features = getPlanFeatures(plan.planCode);
      
      // 🎁 Agregar Planeador 2026 AL INICIO para todos los planes (indica que es solo anuales)
      features = [
        '📅 En planes anuales Incluye Planeador Numerológico 2026 (Se entrega en digital a partir del 10 de diciembre)',
        ...features
      ];

      return {
        ...plan,
        monthlyPrice,
        annualPrice,
        promo,
        features
      };
    });
  }, [plansResponse]);

  const promoPlan = useMemo(() => pricingPlans.find((plan) => plan.promo), [pricingPlans]);

  const promoHeadline = promoPlan
    ? `Promo ${promoPlan.displayName}: ${formatCurrency(promoPlan.promo.price)} anual hasta ${promoPlan.promo.endAt ? new Date(promoPlan.promo.endAt).toLocaleDateString('es-MX') : 'fecha limitada'}`
    : 'Calendario numerológico alineado contigo';

  // Frases inspiradoras para cada número
  const energyPhrases = {
    1: "Día de nuevos comienzos y liderazgo. Tu iniciativa marca la diferencia.",
    2: "Energía de colaboración y sensibilidad. La armonía guía tus pasos.",
    3: "Creatividad y expresión florecen hoy. Comparte tu luz con el mundo.",
    4: "Estructura y disciplina traen logros. Construye con paciencia y firmeza.",
    5: "Libertad y cambio están en el aire. Abraza lo nuevo con entusiasmo.",
    6: "Amor y responsabilidad se entrelazan. Cuida de ti y de los tuyos.",
    7: "Introspección y sabiduría te llaman. Conecta con tu verdad interior.",
    8: "Poder y abundancia se manifiestan. Tu esfuerzo rinde frutos materiales.",
    9: "Compasión y cierre de ciclos. Suelta y transforma con amor.",
    11: "Iluminación espiritual y alta vibración. Tu intuición es tu guía maestra.",
    22: "Maestría y construcción de legados. Tus sueños se vuelven realidad tangible."
  };

  // Obtener fecha formateada
  const getFormattedDate = () => {
    const now = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return now.toLocaleDateString('es-ES', options);
  };

  // Funciones de navegación
  const handleLoginClick = () => navigate('/login');
  const handleRegisterClick = () => navigate('/register');

  // Números flotantes animados de fondo
  const FloatingNumbers = () => {
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22];
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
        {numbers.map((num, i) => (
          <motion.div
            key={i}
            className="absolute text-6xl font-bold text-morado"
            initial={{ 
              x: Math.random() * window.innerWidth, 
              y: -100,
              rotate: Math.random() * 360 
            }}
            animate={{ 
              y: window.innerHeight + 100,
              rotate: Math.random() * 360 + 360
            }}
            transition={{ 
              duration: 15 + Math.random() * 10, 
              repeat: Infinity,
              delay: Math.random() * 5
            }}
          >
            {num}
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-morado/5 relative overflow-hidden">
      <FloatingNumbers />
      
      {/* Botón flotante de WhatsApp */}
      <motion.a
        href="https://wa.me/1234567890?text=Hola,%20quiero%20conocer%20más%20sobre%20Numerathum"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring" }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <MessageCircle className="w-8 h-8 text-white" />
      </motion.a>

      {/* Navbar */}
      <nav className="relative z-40 bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div 
              className="flex items-center gap-2 md:gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <BrandLogo className="w-40 sm:w-56" />
            </motion.div>
            
            <div className="flex items-center gap-3">
              <motion.button
                onClick={handleLoginClick}
                className="px-4 py-2 text-morado hover:bg-morado/10 rounded-lg transition"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Iniciar Sesión
              </motion.button>
              <motion.button
                onClick={handleRegisterClick}
                className="px-6 py-2 bg-morado text-white rounded-lg hover:shadow-lg transition"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Obtener Suscripción
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge de preventa */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-morado/10 border border-morado/30 rounded-full mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Zap className="w-4 h-4 text-morado" />
              <span className="text-sm font-semibold text-morado">
                {promoHeadline}
              </span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold text-morado mb-6 leading-tight">
              Descubre tu energía
              <span className="block text-morado">
                numerológica diaria
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Recibe orientación personalizada en <strong>Trabajo, Amor, Bienestar y Espiritualidad</strong> basada en tu numerología personal. Toma decisiones más conscientes cada día.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                onClick={handleRegisterClick}
                className="px-8 py-4 bg-morado text-white text-lg font-semibold rounded-xl hover:shadow-2xl transition-all flex items-center justify-center gap-2"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Obtener Suscripción
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-white border-2 border-morado text-morado text-lg font-semibold rounded-xl hover:bg-morado/5 transition"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Ver cómo funciona
              </motion.button>
            </div>

            {/* Social Proof */}
            <div className="mt-12 flex items-center gap-6">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-morado border-2 border-white" />
                ))}
              </div>
              <div>
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" />
                  ))}
                </div>
                <p className="text-sm text-gray-600">
                  <strong>500+</strong> usuarios transformando su vida diaria
                </p>
              </div>
            </div>
          </motion.div>

          {/* Hero Image/Mockup */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative">
              {/* Círculo decorativo */}
              <div className="absolute inset-0 bg-morado/20 rounded-full blur-3xl" />
              
              {/* Mockup card */}
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-gray-200">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 mx-auto rounded-full bg-morado flex items-center justify-center mb-4">
                    <span className="text-3xl font-bold text-white">{todayUniversalEnergy}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-morado mb-2">La Energía Universal</h3>
                  <p className="text-gray-600 text-sm mb-3">{getFormattedDate()}</p>
                  <p className="text-morado font-medium text-sm italic leading-relaxed px-4">
                    "{energyPhrases[todayUniversalEnergy]}"
                  </p>
                </div>
                
                <div className="space-y-4">
                  {[
                    { icon: Briefcase, title: "Trabajo", color: "blue" },
                    { icon: Heart, title: "Amor", color: "pink" },
                    { icon: Sparkles, title: "Bienestar", color: "green" },
                    { icon: Target, title: "Espiritual", color: "purple" }
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-${item.color}-100 flex items-center justify-center`}>
                        <item.icon className={`w-5 h-5 text-${item.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">{item.title}</div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-morado"
                            initial={{ width: 0 }}
                            animate={{ width: `${60 + i * 10}%` }}
                            transition={{ delay: 0.8 + i * 0.1, duration: 0.5 }}
                          />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Beneficios Section */}
      <section className="relative z-10 py-20 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-morado mb-4">
              ¿Por qué Numerathum?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Transforma tu día a día con orientación numerológica precisa y personalizada
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Calendar,
                title: "Guía Diaria Personalizada",
                description: "Mensajes únicos basados en tu fecha de nacimiento, ocupación y estado civil",
                color: "bg-blue-500"
              },
              {
                icon: TrendingUp,
                title: "Decisiones Más Conscientes",
                description: "Aprovecha las energías del día para tomar mejores decisiones en tu vida",
                color: "bg-morado"
              },
              {
                icon: Heart,
                title: "4 Áreas de Tu Vida",
                description: "Consejos específicos para Trabajo, Amor, Bienestar y Espiritualidad",
                color: "bg-pink-500"
              },
              {
                icon: Zap,
                title: "Actualizaciones Instantáneas",
                description: "Consulta la energía de cualquier día del mes con un solo clic",
                color: "bg-amber-500"
              },
              {
                icon: Shield,
                title: "100% Privado y Seguro",
                description: "Tus datos están protegidos. Solo tú tienes acceso a tu información",
                color: "bg-green-500"
              },
              {
                icon: Users,
                title: "Comunidad Consciente",
                description: "Únete a cientos de personas que ya transformaron su día a día",
                color: "bg-indigo-500"
              }
            ].map((benefit, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-gray-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className={`w-16 h-16 rounded-2xl ${benefit.color} flex items-center justify-center mb-6`}>
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-morado mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo Funciona */}
      <section id="how-it-works" className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-morado mb-4">
              Cómo Funciona
            </h2>
            <p className="text-xl text-gray-600">
              3 pasos simples para comenzar tu transformación
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Crea tu Perfil",
                description: "Responde unas preguntas simples: nombre, fecha de nacimiento, ocupación y estado civil",
                icon: Users
              },
              {
                step: "2",
                title: "Recibe tu Análisis",
                description: "Nuestro sistema calcula tu numerología personal y universal del día",
                icon: Sparkles
              },
              {
                step: "3",
                title: "Aplica los Consejos",
                description: "Lee tus mensajes diarios personalizados y toma decisiones más conscientes",
                icon: Target
              }
            ].map((step, i) => (
              <motion.div
                key={i}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2 }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative z-10">
                  <div className="absolute -top-6 left-8 w-12 h-12 rounded-full bg-morado flex items-center justify-center text-white text-xl font-bold">
                    {step.step}
                  </div>
                  
                  <div className="mt-8 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-morado/10 flex items-center justify-center">
                      <step.icon className="w-8 h-8 text-morado" />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-morado mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-morado z-0" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative z-10 py-20 bg-morado/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-morado mb-4">
              Planes y Precios
            </h2>
            <p className="text-xl text-gray-600 mb-4">
              Elige el horizonte que mejor se adapta a tu camino
            </p>
            {promoPlan ? (
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-morado text-white rounded-full font-semibold text-lg shadow-lg">
                <Zap className="w-5 h-5" />
                {describePromo(promoPlan.promo)}
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-morado/10 text-morado rounded-full font-semibold text-lg shadow">
                <Calendar className="w-5 h-5" />
                Calendario diario, semanal y mensual en todos los planes
              </div>
            )}
          </motion.div>

          {plansLoading ? (
            <div className="flex justify-center">
              <div className="flex items-center gap-3 px-6 py-4 bg-white rounded-2xl shadow border border-[#d4af37]/30">
                <div className="w-6 h-6 border-4 border-morado border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-morado font-medium">Cargando catálogo de planes...</span>
              </div>
            </div>
          ) : plansError ? (
            <div className="max-w-3xl mx-auto text-center bg-red-50 border border-red-200 rounded-2xl px-6 py-5 text-red-700">
              {plansError}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto overflow-visible">
              {pricingPlans.map((plan, index) => {
                const hasPromo = Boolean(plan.promo);
                const annualDisplayPrice = hasPromo ? plan.promo.price : plan.annualPrice;
                const annualOriginalPrice = hasPromo ? plan.annualPrice : null;
                return (
                  <motion.div
                    key={plan.planCode}
                    className={`relative bg-white rounded-3xl p-8 shadow-xl border-2 z-10 hover:z-20 overflow-visible ${
                      hasPromo ? 'border-morado scale-100 md:scale-105' : 'border-gray-200'
                    }`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    {hasPromo && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 bg-morado text-white rounded-full font-semibold text-sm shadow-lg">
                        Promo activa
                      </div>
                    )}

                    <div className="text-center mb-8 overflow-visible">
                      <h3 className="text-2xl font-bold text-morado mb-2">
                        {plan.displayName}
                      </h3>
                      {plan.tagline && (
                        <p className="text-gray-600 mb-4">{plan.tagline}</p>
                      )}

                      <div className="space-y-2 overflow-visible">
                        {plan.monthlyPrice && (
                          <div className="text-sm text-gray-600">
                            {formatCurrency(plan.monthlyPrice)} <span className="text-xs text-gray-500">/mes</span>
                          </div>
                        )}
                        {annualOriginalPrice && (
                          <div className="text-2xl text-gray-400 line-through">
                            {formatCurrency(annualOriginalPrice)} <span className="text-sm">/año</span>
                          </div>
                        )}
                        {annualDisplayPrice && (
                          <div className="text-4xl font-bold text-morado">
                            {formatCurrency(annualDisplayPrice)} <span className="text-xl text-gray-600 font-normal">/año</span>
                          </div>
                        )}
                        {hasPromo && (
                          <div className="text-xs font-semibold text-morado">
                            {describePromo(plan.promo)}
                          </div>
                        )}
                      </div>
                    </div>

                    <ul className="space-y-4 mb-8 overflow-visible">
                      {plan.features.map((feature, idx) => {
                        const hasNinjaMention = feature.includes('Perfil Adicional') || feature.includes('Perfiles Adicionales');
                        return (
                          <li key={idx} className="flex items-start gap-3 overflow-visible">
                            <div className="w-5 h-5 rounded-full bg-morado/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-3 h-3 text-morado" />
                            </div>
                            <span className="text-gray-700 text-sm flex-1 flex items-center gap-2 overflow-visible">
                              {feature}
                              {hasNinjaMention && (
                                <div className="relative inline-block group overflow-visible">
                                  <HelpCircle className="w-4 h-4 text-morado cursor-help flex-shrink-0" />
                                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-[9999] w-64 pointer-events-none">
                                    <div className="bg-morado text-white text-xs rounded-lg p-3 shadow-xl border-2 border-white">
                                      <p className="font-semibold mb-1">¿Qué es un Perfil Adicional (Ninja)?</p>
                                      <p className="text-white/90">Es una función que te permite agregar y consultar la información numerológica de otra persona (como tu pareja, hijos, familiares o clientes) y puedas también ver la información de sus días y energías numerológicas.</p>
                                      <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-morado rotate-45 border-r border-b border-white"></div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>

                    <motion.button
                      onClick={handleRegisterClick}
                      className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
                        hasPromo
                          ? 'bg-morado text-white hover:shadow-xl'
                          : 'bg-morado/10 text-morado hover:bg-morado/20'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Quiero {plan.displayName}
                    </motion.button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Testimonios */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-morado mb-4">
              Lo que dicen nuestros usuarios
            </h2>
            <p className="text-xl text-gray-600">
              Historias reales de transformación
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "María González",
                role: "Emprendedora",
                image: "💼",
                text: "Numerathum me ayudó a tomar decisiones más acertadas en mi negocio. Los consejos diarios son precisos y aplicables. ¡Increíble!",
                rating: 5
              },
              {
                name: "Carlos Ramírez",
                role: "Diseñador",
                image: "🎨",
                text: "La orientación en amor y relaciones ha sido transformadora. Ahora entiendo mejor mis ciclos emocionales y energéticos.",
                rating: 5
              },
              {
                name: "Ana Martínez",
                role: "Terapeuta",
                image: "🌿",
                text: "Como profesional del bienestar, recomiendo Numerathum a mis clientes. Es una herramienta poderosa para el autoconocimiento.",
                rating: 5
              }
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-[#d4af37] text-[#d4af37]" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-morado flex items-center justify-center text-2xl">
                    {testimonial.image}
                  </div>
                  <div>
                    <div className="font-semibold text-morado">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="relative z-10 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="bg-morado rounded-3xl p-12 text-center text-white shadow-2xl"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Listo para transformar tu vida?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Únete a cientos de personas que ya están viviendo con más consciencia y propósito
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={handleRegisterClick}
                className="px-8 py-4 bg-white text-morado text-lg font-semibold rounded-xl hover:bg-gray-100 transition flex items-center justify-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Obtener Suscripción Ahora
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-morado text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BrandLogo className="w-52 rounded-lg bg-white p-3" />
              </div>
              <p className="text-white/70">
                Tu guía numerológica diaria para una vida más consciente.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Producto</h4>
              <ul className="space-y-2 text-white/70">
                <li><a href="#" className="hover:text-white transition">Características</a></li>
                <li><a href="#" className="hover:text-white transition">Precios</a></li>
                <li><a href="#" className="hover:text-white transition">Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Empresa</h4>
              <ul className="space-y-2 text-white/70">
                <li><a href="#" className="hover:text-white transition">Sobre Nosotros</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Contacto</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-white/70">
                <li><a href="#" className="hover:text-white transition">Privacidad</a></li>
                <li><a href="#" className="hover:text-white transition">Términos</a></li>
                <li><a href="#" className="hover:text-white transition">Cookies</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/70">
            <p className="mb-2">© 2025 Numerathum. Todos los derechos reservados.</p>
            <p className="text-sm">
              Un producto de{' '}
              <a
                href="https://numerologia-cotidiana.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-[#d4af37] transition-colors underline"
              >
                Numerología Cotidiana de Laura L. Rodríguez
              </a>
              {' '}2025
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
